<template>
  <modal
    :value="isShown"
    :validation="data.formValidation"
    size="large"
    @close="closeModal"
    @submit="submit"
  >
    <template #title>
      <slot name="title" />
    </template>
    <template
      v-if="data.localValue"
      #body>
      <div class="flex flex-col w-full">
        <z-input
          id="name"
          v-model:value="data.localValue.name"
          :timeout="0"
          :disabled="isEditedTestAccount"
          class="mb-4 w-full"
          placeholder="Enter your shipping account name"
        >
          <template #label>
            Name
          </template>
        </z-input>
        <z-select
          id="carrier"
          v-model:value="data.localValue.carrier"
          :options="carrierOptions"
          :disabled="!isCreateModal"
          class="mb-4 w-full"
        >
          <template #label>
            Carrier
          </template>
        </z-select>
        <z-select
          id="location_id"
          v-model:value="data.localValue.location_id"
          :options="formattedLocationOptions"
          :disabled="isEditedTestAccount"
          class="mb-4 w-full"
        >
          <template #label>
            Shipping Address
            <router-link
              :to="{ name: 'locations' }"
              class="text-blue-500 hover:text-blue-700"
            >
              New Location
            </router-link>
          </template>
        </z-select>
        <z-input
          v-if="!isCreateModal"
          id="newstore_token"
          v-model:value="data.localValue.newstore_token"
          :timeout="0"
          :disabled="true"
          class="mb-4 w-full"
        >
          <template #label>
            Newstore Auth Token
          </template>
        </z-input>
        <checkbox
          id="testAccount"
          :value="data.localValue.test"
          :disabled="isTestCheckboxDisabled"
          class="mb-4"
          @update:value="updateTestField"
        >
          <template #label>
            Test Account
          </template>
        </checkbox>

        <template
          v-if="(!data.localValue.test || !hasMasterTestAccount) && data.formFields.length"
        >
          <div>
            <h3
              class="text-lg leading-6 font-medium text-gray-900 sm:border-b sm:border-gray-200 sm:pb-3 sm:mb-3"
            >
              Credentials
            </h3>
          </div>
          <template
            v-for="{
              type,
              name,
              label,
              options: fieldOptions,
            } in data.formFields"
            :key="name"
          >
            <z-select
              v-if="type === 'collection'"
              :id="`auth-${name}`"
              v-model:value="data.localValue.auth[name]"
              :options="fieldOptions"
              class="mb-4 w-full"
            >
              <template #label>
                {{ label }}
              </template>
            </z-select>
            <z-input
              v-else
              :id="`auth-${name}`"
              v-model:value="data.localValue.auth[name]"
              :timeout="0"
              class="mb-4 w-full"
            >
              <template #label>
                {{ label }}
              </template>
            </z-input>
          </template>
        </template>

        <template
          v-if="isBillingAddressRequired && data.localValue.billing_address"
        >
          <div>
            <h3
              class="text-lg leading-6 font-medium text-gray-900 sm:border-b sm:border-gray-200 sm:pb-3 sm:mb-3"
            >
              Billing Address
            </h3>
          </div>
          <z-input
            id="billing_address.name"
            v-model:value="data.localValue.billing_address.name"
            :timeout="0"
            :disabled="isEditedTestAccount"
            class="mb-4 w-full"
            placeholder="Enter name"
          >
            <template #label>
              Name
            </template>
          </z-input>
          <z-input
            id="billing_address.company"
            v-model:value="data.localValue.billing_address.company"
            :timeout="0"
            :disabled="isEditedTestAccount"
            class="mb-4 w-full"
            placeholder="Enter company name"
          >
            <template #label>
              Company
            </template>
          </z-input>
          <z-input
            id="billing_address.street1"
            v-model:value="data.localValue.billing_address.street1"
            :timeout="0"
            :disabled="isEditedTestAccount"
            class="mb-4 w-full"
            placeholder="Enter street address"
          >
            <template #label>
              Street Address
            </template>
          </z-input>
          <z-input
            id="billing_address.city"
            v-model:value="data.localValue.billing_address.city"
            :timeout="0"
            :disabled="isEditedTestAccount"
            class="mb-4 w-full"
            placeholder="Enter city"
          >
            <template #label>
              City
            </template>
          </z-input>
          <z-input
            id="billing_address.state"
            v-model:value="data.localValue.billing_address.state"
            :timeout="0"
            :disabled="isEditedTestAccount"
            class="mb-4 w-full"
            placeholder="Enter state/province"
          >
            <template #label>
              State/Province
            </template>
          </z-input>
          <z-input
            id="billing_address.postal_code"
            v-model:value="data.localValue.billing_address.postal_code"
            :timeout="0"
            :disabled="isEditedTestAccount"
            class="mb-4 w-full"
            placeholder="Enter postal code"
          >
            <template #label>
              Postal Code
            </template>
          </z-input>
          <z-select
            id="billing_address.country"
            v-model:value="data.localValue.billing_address.country"
            :options="countryOptions"
            :disabled="isEditedTestAccount"
            class="mb-4 w-full"
          >
            <template #label>
              Country
            </template>
          </z-select>
          <z-input
            id="billing_address.phone"
            v-model:value="data.localValue.billing_address.phone"
            :timeout="0"
            :disabled="isEditedTestAccount"
            class="mb-4 w-full"
            placeholder="Enter phone number"
          >
            <template #label>
              Phone
            </template>
          </z-input>
          <z-input
            id="billing_address.email"
            v-model:value="data.localValue.billing_address.email"
            :timeout="0"
            :disabled="isEditedTestAccount"
            class="mb-4 w-full"
            placeholder="Enter email address"
          >
            <template #label>
              Email
            </template>
          </z-input>
        </template>
        <div>
          <h3
            class="text-lg leading-6 font-medium text-gray-900 sm:border-b sm:border-gray-200 sm:pb-3 sm:mb-3"
          >
            Defaults
          </h3>
        </div>
        <z-select
          id="country"
          :value="data.localValue.country"
          :options="countryOptions"
          :disabled="isEditedTestAccount"
          class="mb-4 w-full"
          @update:value="updateCountry"
        >
          <template #label>
            Country
          </template>
        </z-select>
        <z-select
          id="currency"
          v-model:value="data.localValue.defaults.currency"
          :options="currencyOptions"
          :disabled="isEditedTestAccount"
          class="mb-4 w-full"
        >
          <template #label>
            Currency
          </template>
        </z-select>
        <!--      <z-select-->
        <!--        v-model:value="localValue.billing_address"-->
        <!--        id="billingAddress"-->
        <!--        class="mb-4 w-full">-->
        <!--        <template #label>Billing Address <a href="">New Location</a></template>-->
        <!--      </z-select>-->
        <!--      <z-select-->
        <!--        v-model:value="localValue.shipping_address"-->
        <!--        id="shippingAddress"-->
        <!--        class="mb-4 w-full">-->
        <!--        <template #label>Shipping Address <a href="">New Location</a></template>-->
        <!--      </z-select>-->
        <z-select
          id="defaults.dim_units"
          v-model:value="data.localValue.defaults.dim_units"
          :options="dimensionUnits"
          :disabled="isEditedTestAccount"
          class="mb-4 w-full"
        >
          <template #label>
            Dimension
          </template>
        </z-select>
        <z-select
          id="defaults.weight_units"
          v-model:value="data.localValue.defaults.weight_units"
          :class="data.localValue.carrier ? 'mb-4' : 'mb-6'"
          :options="weightUnits"
          :disabled="isEditedTestAccount"
        >
          <template #label>
            Weight Unit
          </template>
        </z-select>
        <z-select
          id="defaults.packaging_type"
          v-model:value="data.localValue.defaults.packaging_type"
          :options="packagingTypeOptions"
          :disabled="isEditedTestAccount || !data.localValue.carrier"
          class="mb-4"
          options-top-position
        >
          <template #label>
            Packaging Type
          </template>
        </z-select>
        <z-select
          id="defaults.service"
          v-model:value="data.localValue.defaults.service"
          :options="serviceTypeOptions"
          :disabled="isEditedTestAccount || !data.localValue.carrier"
          class="mb-4"
          options-top-position
        >
          <template #label>
            Service Type
          </template>
        </z-select>
        <z-select
          id="defaults.label_type"
          v-model:value="data.localValue.defaults.label_type"
          :options="labelTypeOptions"
          :disabled="isEditedTestAccount || !data.localValue.carrier"
          class="mb-4"
          options-top-position
        >
          <template #label>
            Label Type
          </template>
        </z-select>
      </div>
      <!--      <z-select-->
      <!--        v-model:value="data.localValue.currency"-->
      <!--        id="currency"-->
      <!--        class="mb-4 w-full">-->
      <!--        <template #label>Default Currency</template>-->
      <!--      </z-select>-->
    </template>
    <template #footer>
      <z-button
        :disabled="value.test"
        type="success"
        full-width
        submit>
        <slot name="send-button-label" />
      </z-button>
    </template>
  </modal>
</template>

<script>
export default {
  name: "ShippingAccountModal",
};
</script>

<script setup>
import {
  computed,
  defineEmits,
  defineProps,
  nextTick,
  reactive,
  watch,
} from "vue";
import { string, object } from "yup";

import ZInput from "../../atoms/Input";
import ZButton from "../../atoms/Button";
import ZSelect from "../../atoms/Select";
import Checkbox from "../../atoms/Checkbox";
import Modal from "../../organisms/Modal";

const requiredBaseFieldsSchema = {
  name: string().max(50).required().label("Name"),
  carrier: string().required().label("Carrier"),
  country: string().required().label("Country"),
  defaults: object({
    dim_units: string().required().label("Dimension"),
    weight_units: string().required().label("Weight Unit"),
    // packaging_type: string().required().label('Packaging Type'),
    // service: string().required().label('Service'),
    // label_type: string().required().label('Label Type'),
  }),
  location_id: string().label("Shipping Address"),
};

const billingAddressSchema = {
  billing_address: object({
    name: string().required().label("Name"),
    company: string().label("Company"),
    street1: string().required().label("Street Address"),
    city: string().required().label("City"),
    state: string().label("State/Province"),
    postal_code: string().required().label("Postal Code"),
    country: string().required().label("Country"),
    phone: string().required().label("Phone"),
    email: string().email().label("Email"),
  })
    .required()
    .label("Billing Address"),
};

const dimensionUnits = ["CM", "IN"];
const weightUnits = ["KG", "LB"];

const props = defineProps({
  value: {
    type: Object,
    default: () => ({}),
  },
  carriers: {
    type: Array,
    default: () => [],
  },
  locations: {
    type: Array,
    default: () => [],
  },
  countryOptions: {
    type: Array,
    default: () => [],
  },
  currencyOptions: {
    type: Array,
    default: () => [],
  },
  packagingTypes: {
    type: Object,
    default: () => ({}),
  },
  serviceTypes: {
    type: Object,
    default: () => ({}),
  },
  labelTypes: {
    type: Object,
    default: () => ({}),
  },
  isShown: {
    type: Boolean,
    default: false,
  },
  isCreateModal: {
    type: Boolean,
    default: true,
  },
});

const localValueFactory = () => ({
  name: "",
  carrier: "",
  country: "",
  defaults: {
    dim_units: "",
    weight_units: "",
    packaging_type: "",
    service: "",
    label_type: "",
    currency: "",
  },
  location_id: "",
  auth: {},
  test: false,
});

const billingAddressFactory = () => ({
  name: "",
  company: "",
  street1: "",
  city: "",
  state: "",
  postal_code: "",
  country: "",
  phone: "",
  email: "",
});

const getBillingAddressFromAuth = (carrierData) => {
  if (!carrierData || !carrierData.auth) {
    return billingAddressFactory();
  }

  const auth = carrierData.auth;

  return {
    name: (auth && auth.billing_name) || "",
    company: (auth && auth.billing_company) || "",
    street1: (auth && auth.billing_street1) || "",
    city: (auth && auth.billing_city) || "",
    state: (auth && auth.billing_state) || "",
    postal_code: (auth && auth.billing_postal_code) || "",
    country: (auth && auth.billing_country) || "",
    phone: (auth && auth.billing_phone) || "",
    email: (auth && auth.billing_email) || "",
  };
};

const data = reactive({
  localValue: {
    ...localValueFactory(),
    ...props.value,
  },
  selectedCarrier: {},
  formValidation: object({ ...requiredBaseFieldsSchema }),
  formFields: [],
});

const carrierOptions = computed(() =>
  props.carriers
    .map((carrier) => ({
      value: carrier.slug,
      label:
        carrier.display_name.trim() ||
        carrier.slug[0].toUpperCase() + carrier.slug.substr(1),
    }))
    .sort((a, b) => {
      const nameA = a.label.toUpperCase();
      const nameB = b.label.toUpperCase();

      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    })
);

const packagingTypeOptions = computed(
  () => props.packagingTypes[data.localValue.carrier]
);
const labelTypeOptions = computed(
  () => props.labelTypes[data.localValue.carrier]
);
const serviceTypeOptions = computed(
  () => props.serviceTypes[data.localValue.carrier]
);
const isEditedTestAccount = computed(
  () => !props.isCreateModal && props.value.test
);
const formattedLocationOptions = computed(() =>
  props.locations.map(({ id: value, name: label }) => ({ value, label }))
);

const hasMasterTestAccount = computed(
  () =>
    data.selectedCarrier.has_master_account &&
    data.selectedCarrier.master_account_countries &&
    data.selectedCarrier.master_account_countries.includes(
      data.localValue.country
    )
);

const isTestCheckboxDisabled = computed(
  () =>
    !props.isCreateModal &&
    props.value.test &&
    hasMasterTestAccount.value
);

const isBillingAddressRequired = computed(
  () =>
    data.selectedCarrier.billing_address_required === true &&
    props.isCreateModal
);

watch(
  () => props.value,
  (value) => {
    const newLocalValue = localValueFactory();
    data.localValue = {
      ...newLocalValue,
      ...value,
      auth: {
        ...newLocalValue.auth,
        ...value.auth,
      },
      defaults: {
        ...newLocalValue.defaults,
        ...value.defaults,
      },
    };

    if (isBillingAddressRequired.value) {
      data.localValue.billing_address = {
        ...getBillingAddressFromAuth(value),
      };
    }
  }
);

watch(
  () => data.localValue.carrier,
  async (value) => {
    data.localValue.test = false;
    data.localValue.packaging_type = "";
    data.localValue.service = "";
    data.localValue.label_type = "";

    if (!value) {
      data.selectedCarrier = {};
      if (data.localValue && data.localValue.billing_address) {
        delete data.localValue.billing_address;
      }
      await rebuildForm();
      return;
    }

    for (let i = 0; i < props.carriers.length; ++i) {
      if (props.carriers[i].slug === value) {
        data.selectedCarrier = props.carriers[i];
        await rebuildForm(!!Object.keys(props.value).length);
        break;
      }
    }
  }
);

const emit = defineEmits(["send", "close"]);

const rebuildForm = async (isEdited) => {
  data.formFields = [];
  data.form = object({});

  await nextTick(() => {
    let form = { ...requiredBaseFieldsSchema };
    const fields = [];

    if (isBillingAddressRequired.value) {
      form = { ...form, ...billingAddressSchema };

      if (data.localValue && !data.localValue.billing_address) {
        data.localValue.billing_address = billingAddressFactory();
      }
    }

    const { auth } = data.selectedCarrier;
    const showCredentials =
      !data.localValue.test || !hasMasterTestAccount.value;
    auth &&
      Array.isArray(auth) &&
      showCredentials &&
      auth.forEach((element) => {
        if (!element.ui) return;

        const key = `auth-${element.slug}`;
        form[key] = string();

        if (element.required) {
          form[key] = form[key].required();
        }
        form[key] = form[key].label(element.name);

        const fieldItem = {
          name: element.slug,
          label: element.name,
          type: element.type,
        };
        if ("collection" in element) {
          fieldItem["options"] = element["collection"].map((item) => ({
            value: item.slug,
            label: item.name,
          }));
        }

        fields.push(fieldItem);
      });

    if (!Object.keys(props.value.auth || {}) || !isEdited) {
      data.localValue.auth = {};
    }

    data.formValidation = object(form);
    data.formFields = fields;
  });
};

const updateTestField = async (current) => {
  const previous = data.localValue.test;
  data.localValue.test = current;

  if (current || (previous && !current)) {
    await rebuildForm();
  }
};

const updateCountry = async (value) => {
  data.localValue.country = value;
  await updateTestField(false);
};

const submit = () => emit("send", data.localValue);

const closeModal = async () => {
  data.localValue = localValueFactory();
  await nextTick(async () => {
    await nextTick(() => emit("close"));
  });
};
</script>
